import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Check, Send, ArrowRight, Heart } from "lucide-react";
import { ProgramBuilder } from "@/components/ProgramBuilder";

const BUILDER_KEYS = new Set(["corporate-care", "church-ministry", "hospital-partnerships"]);

export const PROGRAMS = {
  "care-boxes": {
    label: "H&F Care Boxes",
    title: "H&F Care Boxes",
    subtitle: "Our signature curated comfort — ready to send.",
    image: "https://images.unsplash.com/photo-1513201099705-a9746e1e201f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    intro: "Every Care Box is thoughtfully assembled by hand for a specific season — illness, grief, anxiety, recovery, caregiving, and encouragement — and finished with our signature Hope Card. It's the simplest way to say 'I'm here' when words fall short.",
    features: ["12 curated boxes across life's hardest moments", "Signature hand-written Hope Card in every box", "Personal message + QR welcome video included", "Add premium upgrades at checkout"],
    ctaLabel: "Shop the Care Boxes",
    ctaTo: "/shop",
    showForm: false,
  },
  "hope-cards": {
    label: "H&F Hope Cards",
    title: "H&F Hope Cards",
    subtitle: "Encouragement worth keeping — and collecting.",
    image: "https://images.unsplash.com/photo-1481011784351-b0227b9f4a80?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    intro: "Our Hope Cards aren't tossed after a day — they're tucked into drawers, taped to mirrors, and rediscovered on hard days. Each design carries a gentle message and a QR code to a welcome video. Collect the series, or send one at a time.",
    features: ["Beautifully designed, keepsake-quality cards", "A growing collectible series", "QR code to a short welcome video", "Perfect for churches, teams & individuals"],
    ctaLabel: "Explore the Shop",
    ctaTo: "/shop",
    showForm: true,
    formTitle: "Interested in collecting or reselling Hope Cards?",
  },
  "corporate-care": {
    label: "H&F Corporate Care Program",
    title: "H&F Corporate Care Program",
    subtitle: "Show your people you truly see them.",
    image: "https://images.unsplash.com/photo-1758691737138-7b9b1884b1db?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    intro: "When an employee faces illness, loss, or a hard season, a thoughtful care package says what a company email never could. Our Corporate Care Program makes it effortless to send meaningful comfort — with volume pricing, custom branding, and simple ordering for HR and People teams.",
    features: ["Volume & recurring pricing for teams", "Optional co-branded Hope Cards", "Simple bulk & recurring ordering", "Dedicated account support"],
    ctaLabel: "",
    ctaTo: "",
    showForm: true,
    formTitle: "Tell us about your team",
  },
  "church-ministry": {
    label: "H&F Church Ministry Boxes",
    title: "H&F Church Ministry Boxes",
    subtitle: "Help your congregation care well.",
    image: "https://images.unsplash.com/photo-1438232992991-995b7058bbb3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    intro: "Equip your care team, deacons, and ministry leaders to show up for members walking through illness, grief, and hardship. Our Church Ministry Boxes come ready to bless — with faith-centered comfort and optional scripture personalization.",
    features: ["Faith-centered comfort boxes", "Ministry & bulk pricing", "Optional scripture personalization", "Great for care teams & outreach"],
    ctaLabel: "",
    ctaTo: "",
    showForm: true,
    formTitle: "Start a ministry partnership",
  },
  "hospital-partnerships": {
    label: "H&F Hospital Partnerships",
    title: "H&F Hospital Partnerships",
    subtitle: "Comfort at the bedside, made simple.",
    image: "https://images.pexels.com/photos/5875563/pexels-photo-5875563.jpeg?auto=compress&cs=tinysrgb&w=1200",
    intro: "Partner with Hope & Favor to offer patients and families a moment of comfort during difficult stays. Ideal for oncology units, hospice, patient-experience teams, and foundations — with fulfillment and gifting programs tailored to your facility.",
    features: ["Programs for oncology, hospice & patient experience", "Bulk & recurring fulfillment", "Custom curation for your patients", "Compassion your team can stand behind"],
    ctaLabel: "",
    ctaTo: "",
    showForm: true,
    formTitle: "Explore a hospital partnership",
  },
  "monthly-club": {
    label: "H&F Monthly Encouragement Club",
    title: "H&F Monthly Encouragement Club",
    subtitle: "A little hope, arriving all year long.",
    image: "https://images.unsplash.com/photo-1549465220-1a8b9238cd48?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    intro: "The Encouragement Club sends a fresh dose of comfort and hope on a rhythm you choose — for someone going through a long season, or for yourself. A separate membership offering built around steady, ongoing encouragement.",
    features: ["Choose weekly, monthly, or yearly", "A curated box + Hope Card each delivery", "Members-first pricing & perks", "Pause or cancel anytime"],
    ctaLabel: "View Club Plans",
    ctaTo: "/subscriptions",
    showForm: true,
    formTitle: "Have a question about the Club?",
  },
};

export default function ProgramPage({ k }) {
  const program = PROGRAMS[k];
  const [form, setForm] = useState({ name: "", email: "", organization: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", {
        name: form.name, email: form.email, organization: form.organization, phone: form.phone,
        program: program.label, subject: `${program.label} inquiry`, message: form.message,
      });
      toast.success("Thank you — we'll be in touch soon 💛");
      setForm({ name: "", email: "", organization: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={`program-${k}`}>
      <section className="relative overflow-hidden bg-[#F2EFEB]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="gh-fade-up">
            <span className="gh-overline">Hope &amp; Favor Program</span>
            <h1 className="font-heading text-5xl sm:text-6xl text-[#3A3632] mt-4 leading-tight">{program.title}</h1>
            <p className="font-hand text-3xl text-[#C4A4A4] mt-3">{program.subtitle}</p>
            <div className="gh-hairline my-7 max-w-[120px]" />
            <p className="text-lg text-[#6B655F] leading-relaxed font-light">{program.intro}</p>
            {program.ctaLabel && (
              <Link to={program.ctaTo} data-testid="program-cta" className="btn-primary mt-8 inline-flex">{program.ctaLabel} <ArrowRight className="h-4 w-4" /></Link>
            )}
          </div>
          <div className="relative gh-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="gh-frame overflow-hidden">
              <img src={program.image} alt={program.title} className="w-full h-[460px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {program.features.map((f) => (
            <div key={f} className="bg-white border border-[#D9CBA0]/40 p-6">
              <div className="h-11 w-11 gh-octagon bg-[#8E9A86]/12 flex items-center justify-center mb-4"><Check className="h-5 w-5 text-[#8E9A86]" /></div>
              <p className="text-[#3A3632] leading-relaxed">{f}</p>
            </div>
          ))}
        </div>
      </section>

      {BUILDER_KEYS.has(k) && <ProgramBuilder programKey={k} />}

      {program.showForm && !BUILDER_KEYS.has(k) && (
        <section className="bg-[#3A3632] py-20" data-testid="program-inquiry">
          <div className="max-w-3xl mx-auto px-5 sm:px-8">
            <div className="text-center">
              <span className="gh-overline text-[#D9CBA0]">Let's talk</span>
              <h2 className="font-heading text-3xl sm:text-4xl text-[#FAF7F2] mt-4">{program.formTitle}</h2>
              <p className="text-[#D9CBA0]/90 mt-3">Share a few details and we'll reach out personally.</p>
            </div>
            <form onSubmit={submit} className="mt-10 bg-[#FAF7F2] rounded-2xl p-8" data-testid="program-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block"><span className="text-sm text-[#3A3632] font-medium mb-1.5 block">Your name</span>
                  <input required value={form.name} onChange={set("name")} data-testid="inq-name" className="gh-input" /></label>
                <label className="block"><span className="text-sm text-[#3A3632] font-medium mb-1.5 block">Email</span>
                  <input type="email" required value={form.email} onChange={set("email")} data-testid="inq-email" className="gh-input" /></label>
                <label className="block"><span className="text-sm text-[#3A3632] font-medium mb-1.5 block">Organization</span>
                  <input value={form.organization} onChange={set("organization")} data-testid="inq-org" className="gh-input" /></label>
                <label className="block"><span className="text-sm text-[#3A3632] font-medium mb-1.5 block">Phone <span className="text-[#8A8175] font-normal">(optional)</span></span>
                  <input value={form.phone} onChange={set("phone")} data-testid="inq-phone" className="gh-input" /></label>
              </div>
              <label className="block mt-4"><span className="text-sm text-[#3A3632] font-medium mb-1.5 block">How can we help?</span>
                <textarea required rows={4} value={form.message} onChange={set("message")} data-testid="inq-message" className="gh-input resize-none" /></label>
              <button type="submit" disabled={busy} data-testid="inq-submit" className="w-full mt-4 bg-[#8E9A86] text-white rounded-full px-6 py-3.5 font-medium hover:bg-[#7A8772] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
                <Send className="h-4 w-4" /> {busy ? "Sending…" : "Send inquiry"}
              </button>
            </form>
          </div>
          <style>{`.gh-input{width:100%;border:1px solid #D1CCC5;border-radius:0.6rem;background:#fff;padding:0.7rem 0.9rem;font-size:0.95rem;outline:none;transition:all .2s}.gh-input:focus{border-color:#8E9A86;box-shadow:0 0 0 2px rgba(142,154,134,.25)}`}</style>
        </section>
      )}

      <section className="py-16 text-center">
        <p className="flex items-center justify-center gap-2 text-[#8A8175]"><Heart className="h-4 w-4 text-[#C4A4A4]" /> Helping people care for others, beautifully.</p>
      </section>
    </div>
  );
}
